import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, unstable_useBlocker as useBlockerCompat } from 'react-router-dom';
import {
  AlertTriangle, FileSpreadsheet, ArrowRight, ClipboardList, Play, CheckCircle, ChevronLeft, Search, ListTree,
  BookOpen, Monitor, Printer, Trash2, X,
} from 'lucide-react';
import { useClasseContext } from '../context/ClasseContext';
import { useUser } from '../context/UserContext';
import { useQuestion } from '../hooks/fetch_api_hook/useQuestion';
import { usePostApi } from '../hooks/post_api_hook/usePostApi';
import { usePatchApi } from '../hooks/put_api_hook/usePatchApi';
import { useDeleteQuestion } from '../hooks/delete_api_hook/useDeleteQuestion';
import useConfirm from '../hooks/ui/useConfirm';
import { url } from '../utils/confserveur';
import { buildPreviewContenu, getQuestionTypeLabel } from '../utils/questionUtils';
import { toPayloadsUnifies, versVarianteEvalNat, formDataVariante, formDataCorrection } from '../utils/evalNatAdapter';
import { texteDeLItem } from '../generators/evalnat';
import { supportDeQuestion } from '../generators/evalnat/supports';
import { formeDuNiveau } from '../generators/evalnat/formesParalleles';
import { normalizeMatiere } from '../utils/matiere';
import { nomDuTexte } from '../components/VueEvalNat/EvalNat/evaluationConfig';
import MenuItemsEvalNat from '../components/VueEvalNat/BilanEval/MenuItemsEvalNat';
import ItemEvalNat from '../components/VueEvalNat/BilanEval/ItemEvalNat';
import FormeParalleleEvalNat from '../components/VueEvalNat/BilanEval/FormeParalleleEvalNat';
import LigneTexteLecture from '../components/VueEvalNat/BilanEval/LigneTexteLecture';
import {
  cleItem, enregistrerPuisLancer, filtrerItems, pourLAjout, pourLaCorrection, pourLaFenetre, ranger,
} from '../components/VueEvalNat/BilanEval/selectionEvalNat';
import LaunchModal from '../components/VueQuestion/LaunchModal';
import ApercuEleveModal from '../components/VueQuestion/ApercuEleveModal';
import QuestionForm from '../components/VueQuestion/QuestionForm';
import DetailEvalBilan from '../components/VueEvalNat/BilanEval/DetailEvalBilan';
import QuestionPreviewModal from '../components/VueQuestion/QuestionPreviewModal';

// Hoistés : identités stables entre renders (props du LaunchModal)
const FORMATS_EVALNAT = ['en_ligne', 'papier'];
const LISTE_VIDE = [];
const LIBELLE_MATIERE = { maths: 'Mathématiques', français: 'Français' };
const NIVEAU_MAP = { '6': '6eme', '5': '5eme', '4': '4eme', '3': '3eme' };
const sansBalises = (html) => String(html || '').replace(/<[^>]*>/g, '').trim();
const sansApostropheCourbe = (s) => String(s || '').replace(/’/g, "'");
const numeroItem = (questionNum) => parseInt(String(questionNum || '').replace(/\D/g, ''), 10) || 0;
// L'entrée du menu pour la forme parallèle de la compréhension (retenue comme un item)
const CLE_FORME = 'forme-parallele';

// Le message d'un refus du serveur, s'il en donne un lisible
const messageDuServeur = (erreur, parDefaut) => {
  const donnees = erreur?.response?.data;
  if (typeof donnees?.error === 'string') return donnees.error;
  if (typeof donnees?.detail === 'string') return donnees.detail;
  return parDefaut;
};

// Le dernier item ouvert, retenu par classe (une commodité : la page marche sans)
const lireItemRetenu = (idclasse) => {
  try { return localStorage.getItem(`evalbilan-item-${idclasse}`); } catch { return null; }
};
const retenirItem = (idclasse, cle) => {
  try { localStorage.setItem(`evalbilan-item-${idclasse}`, cle); } catch { /* stockage indisponible */ }
};

/**
 * Le QCM de suivi de l'évaluation nationale (11/10/2026), composé EXACTEMENT comme une session de
 * QCMDashboard, en attendant l'interface commune aux deux : plein écran, le même bandeau collant (retour à la
 * classe, classe, item ouvert), le menu des items à la place du panneau des attendus, la page d'un item comme
 * celle d'un attendu (AttenduExercices, ItemEvalNat), la même barre du bas et le même tiroir, la même fenêtre
 * pour créer une question personnalisée et pour corriger une variante. Les exercices tirés ne sont écrits en
 * base qu'au lancement, comme variantes de leur item (selectionEvalNat.js).
 */
function EvalBilan({ EvalsNat, handleReloadEvalNat }) {
  const { idclasse } = useParams();
  const navigate = useNavigate();
  const { classeNom, groupNom } = useClasseContext();
  const { user, etablissementData } = useUser();

  // EvalNat de référence
  const EvalNatRef = useMemo(() =>
    EvalsNat?.find((e) => e.titre === 'Evaluation Nationale'
      && (groupNom ? true : e.classe === parseInt(idclasse, 10)))
  , [EvalsNat, idclasse, groupNom]);

  // Bilans existants
  const [listEvalsBilan, setListEvalsBilan] = useState([]);
  useEffect(() => {
    if (EvalsNat) {
      setListEvalsBilan(EvalsNat.filter(e => e.titre !== 'Evaluation Nationale'
        && (groupNom ? e.groupe === parseInt(idclasse, 10) : e.classe === parseInt(idclasse, 10))));
    }
  }, [EvalsNat, idclasse, groupNom]);

  // Évaluations "en cours" (au moins 1 élève n'a pas encore répondu)
  const evalsEnCours = useMemo(() => {
    return listEvalsBilan.filter(item => {
      const reponses = item.eleves_reponses || [];
      if (reponses.length === 0) return false;
      const nbReponses = reponses.filter(e => e.reponses != null).length;
      return nbReponses < reponses.length;
    });
  }, [listEvalsBilan]);

  // Les questions en base choisies (officielles, variantes du collège) et le panier des exercices tirés,
  // comme QCMDashboard : clé (item, format) → { payloads }
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectedExercices, setSelectedExercices] = useState(() => new Map());
  const [showDrawer, setShowDrawer] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  // Format pré-positionné dans le LaunchModal unifié (2.4b) :
  // 'en_ligne' (bilan EvalNat) | 'papier' (PDF seul). Pas d'ArUco pour EvalNat
  // (pas d'attendus TanoEd → bulk-create créerait des questions orphelines).
  const [launchFormat, setLaunchFormat] = useState('en_ligne');
  const [launchLoading, setLaunchLoading] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);

  // Détail d'un bilan
  const [selectedEvalId, setSelectedEvalId] = useState(false);
  const [evalBilan, setEvalBilan] = useState(null);

  // Deep link depuis la liste unifiée (3.2) : ?bilan=<id> ouvre le détail du
  // bilan ciblé. La consultation ET l'édition vivent désormais dans
  // Évaluations (l'historique local a été retiré) : cette page n'est plus
  // qu'un écran de création + le détail. Consommé UNE seule fois (ref).
  const [searchParams] = useSearchParams();
  const bilanCibleConsomme = useRef(false);
  useEffect(() => {
    if (bilanCibleConsomme.current) return;
    const cible = parseInt(searchParams.get('bilan'), 10);
    if (!cible || listEvalsBilan.length === 0) return;
    const item = listEvalsBilan.find((e) => e.id === cible);
    if (item) {
      bilanCibleConsomme.current = true;
      setSelectedEvalId(item.id);
      setEvalBilan(item);
    }
  }, [searchParams, listEvalsBilan]);

  // Aperçus : une question en base (fenêtre d'aperçu), un exercice tiré (aperçu élève, régénérable)
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [apercu, setApercu] = useState(null);

  // La fenêtre de QCMDashboard (QuestionForm), pour créer une question d'un item ou corriger une variante
  // en base : { mode: 'creer', officielle } ou { mode: 'corriger', variante }
  const [formulaire, setFormulaire] = useState(null);
  const [erreurFormulaire, setErreurFormulaire] = useState(null);
  const [newlyCreatedId, setNewlyCreatedId] = useState(null);
  // Un exercice modifié qu'une variante ne sait pas porter, refusé à l'ajout
  const [erreurAjout, setErreurAjout] = useState(null);

  const { deleteQuestion } = useDeleteQuestion();
  const { confirm, ConfirmDialog } = useConfirm();

  // Niveau / Matière / Questions EvalNat
  const nomRef = classeNom || groupNom || '';
  const niveau = nomRef.charAt(0) || '';
  const niveauForm = NIVEAU_MAP[niveau] || '';
  const matiere = user?.matiere || '';
  const [refreshQuestions, setRefreshQuestions] = useState(0);
  const { data: rawQuestionsEvalNat, isLoading: loadingQuestions } = useQuestion(
    niveau ? `?classe_concernees=${niveau}` : null,
    refreshQuestions,
    etablissementData?.id
  );

  // Filtrer par matière du prof (maths vs français ont des type_label différents)
  const questionsEvalNat = useMemo(() => {
    if (!Array.isArray(rawQuestionsEvalNat)) return [];
    const mat = normalizeMatiere(matiere);
    if (mat === 'maths') {
      return rawQuestionsEvalNat.filter(q =>
        q.type_label === 'Automatisme' || q.type_label === 'Résolution de problèmes'
      );
    }
    if (mat === 'français') {
      return rawQuestionsEvalNat.filter(q =>
        q.type_label?.includes('Lexique')
        || q.type_label?.includes('Compétences langagières')
        || q.type_label?.startsWith('Compréhension de l\'écrit')
      );
    }
    return rawQuestionsEvalNat;
  }, [rawQuestionsEvalNat, matiere]);

  // POST bilan ; POST d'une variante (question personnalisée, exercices tirés au lancement)
  const [, doPost] = usePostApi(url + 'evaluations_nat/');
  const [, doPostQuestion] = usePostApi(url + 'questions/');

  // Toggle sélection
  const handleToggleSelect = useCallback((qId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(qId)) next.delete(qId);
      else next.add(qId);
      return next;
    });
  }, []);

  // Aperçu question — transformation unifiée via buildPreviewContenu
  const handlePreview = useCallback((q) => {
    setPreviewQuestion(buildPreviewContenu(q));
  }, []);
  const handleApercu = useCallback((question, regenerate) => setApercu({ question, regenerate }), []);

  // Supprimer une variante en base : la confirmation de QCMDashboard
  const handleDeleteVariante = useCallback(async (question) => {
    if (!(await confirm({
      title: 'Supprimer cette variante ?',
      message: 'Elle ne sera plus proposée dans le QCM de suivi. Action irréversible.',
      confirmLabel: 'Supprimer',
      variant: 'danger',
    }))) return;
    await deleteQuestion(question.id);
    setSelectedIds((prev) => {
      if (!prev.has(question.id)) return prev;
      const next = new Set(prev);
      next.delete(question.id);
      return next;
    });
    setRefreshQuestions((prev) => prev + 1);
  }, [confirm, deleteQuestion]);

  // Questions sélectionnées (objets EvalNat bruts)
  const selectedQuestions = useMemo(() => {
    if (!Array.isArray(questionsEvalNat)) return [];
    const map = new Map(questionsEvalNat.map((q) => [q.id, q]));
    return [...selectedIds].map((id) => map.get(id)).filter(Boolean);
  }, [questionsEvalNat, selectedIds]);

  // ─── La forme parallèle de la compréhension (11/10/2026) ───
  // En français, d'autres textes que septembre (generators/evalnat/formesParalleles), aux questions communes à
  // tous les collèges. Dès qu'elles sont en base, elles prennent la place des items de compréhension de
  // septembre dans le menu (les reposer mesurerait la mémoire), et se choisissent d'un bloc, texte par texte.
  const forme = useMemo(() => (normalizeMatiere(matiere) === 'français' ? formeDuNiveau(niveau) : null), [matiere, niveau]);
  const textesForme = useMemo(() => new Set((forme?.textes || []).map((t) => t.typeLabel)), [forme]);
  const estDeLaForme = useCallback((q) => textesForme.has(sansApostropheCourbe(q.type_label)), [textesForme]);
  const questionsParTexte = useMemo(() => {
    const parTexte = new Map((forme?.textes || []).map((t) => [t.typeLabel, []]));
    questionsEvalNat.forEach((q) => { parTexte.get(sansApostropheCourbe(q.type_label))?.push(q); });
    parTexte.forEach((liste) => liste.sort((a, b) => numeroItem(a.question_num) - numeroItem(b.question_num)));
    return parTexte;
  }, [forme, questionsEvalNat]);
  const formeDisponible = Boolean(forme) && [...questionsParTexte.values()].some((liste) => liste.length > 0);
  const masques = useMemo(
    () => new Set([...textesForme, ...(formeDisponible ? forme.remplace.map(sansApostropheCourbe) : [])]),
    [textesForme, formeDisponible, forme]
  );
  const basculerQuestions = useCallback((ids, ajouter) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (ajouter ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  // ─── Le menu des items (à la place du panneau des attendus) ───
  const groupes = useMemo(
    () => ranger(questionsEvalNat.filter((q) => !masques.has(sansApostropheCourbe(q.type_label)))),
    [questionsEvalNat, masques]
  );
  const [recherche, setRecherche] = useState('');
  const groupesFiltres = useMemo(() => filtrerItems(groupes, recherche), [groupes, recherche]);
  // Comme le panneau des attendus : ouvert à l'arrivée tant qu'aucun item n'est retenu pour la classe
  const [itemActif, setItemActif] = useState(() => lireItemRetenu(idclasse));
  const [menuOuvert, setMenuOuvert] = useState(() => !lireItemRetenu(idclasse));
  const itemCourant = useMemo(
    () => groupes.flatMap((g) => g.items).find((i) => cleItem(i.typeLabel, i.questionNum) === itemActif) || null,
    [groupes, itemActif]
  );
  const choisirItem = useCallback((typeLabel, questionNum) => {
    const cle = cleItem(typeLabel, questionNum);
    setItemActif(cle);
    retenirItem(idclasse, cle);
    setMenuOuvert(false);
    setRecherche('');
  }, [idclasse]);
  const choisirForme = useCallback(() => {
    setItemActif(CLE_FORME);
    retenirItem(idclasse, CLE_FORME);
    setMenuOuvert(false);
    setRecherche('');
  }, [idclasse]);
  const surForme = formeDisponible && itemActif === CLE_FORME;

  // ─── Le panier, avec la règle de QCMDashboard (handleUpdateExercice) ───
  // Un ajout à la fois, un compte nul retire la carte. Un exercice ajouté est déjà la variante de son item
  // (pourLAjout : son texte de compréhension, un _tmpId qui ne bouge plus) ; ce qu'une variante ne sait pas
  // porter est refusé ici plutôt qu'au lancement, et la carte, qui reçoit false, garde son aperçu.
  const rangRef = useRef(0);
  const handleUpdateExercice = useCallback((cle, compte, payload, officielle) => {
    if (compte <= 0 || !payload) {
      setSelectedExercices((prev) => {
        if (!prev.has(cle)) return prev;
        const next = new Map(prev);
        next.delete(cle);
        return next;
      });
      return true;
    }
    const ajout = pourLAjout(payload, officielle, {
      texte: texteDeLItem(officielle, niveau),
      format: cle.slice(`evalnat_${officielle.id}_`.length),
      rang: rangRef.current + 1,
    });
    if (!ajout.ok) {
      setErreurAjout(ajout.erreur);
      return false;
    }
    setErreurAjout(null);
    rangRef.current += 1;
    setSelectedExercices((prev) => new Map(prev).set(cle, { payloads: [...(prev.get(cle)?.payloads || []), ajout.element] }));
    return true;
  }, [niveau]);

  // Les exercices tirés, à plat (chacun garde son _tmpId : un nouvel essai de lancement retrouve les
  // variantes déjà enregistrées)
  const generatedItems = useMemo(
    () => [...selectedExercices.values()].flatMap(({ payloads }) => payloads),
    [selectedExercices]
  );

  // Détail des formats sélectionnés pour la barre de statut
  const formatDetail = useMemo(() => {
    const byType = {};
    let total = 0;
    selectedExercices.forEach(({ payloads }) => {
      const count = payloads.length;
      const payload = payloads[0];
      if (!payload) return;
      const label = getQuestionTypeLabel(payload);
      byType[label] = (byType[label] || 0) + count;
      total += count;
    });
    const detail = Object.entries(byType).map(([label, n]) => `${n} ${label}`).join(' · ');
    return { total, detail };
  }, [selectedExercices]);
  const nbOfficielles = selectedQuestions.filter((q) => q.est_nationale).length;
  const nbForme = selectedQuestions.filter(estDeLaForme).length;
  const nbVariantesBanque = selectedQuestions.length - nbOfficielles - nbForme;
  const partiesBanque = [
    nbOfficielles > 0 ? `${nbOfficielles} question${nbOfficielles > 1 ? 's' : ''} officielle${nbOfficielles > 1 ? 's' : ''}` : null,
    nbForme > 0 ? `${nbForme} question${nbForme > 1 ? 's' : ''} de la forme parallèle` : null,
    nbVariantesBanque > 0 ? `${nbVariantesBanque} variante${nbVariantesBanque > 1 ? 's' : ''} du collège` : null,
  ].filter(Boolean);
  const nbChoisis = formatDetail.total + selectedQuestions.length;

  // Le nombre de questions choisies par item (menu)
  const comptes = useMemo(() => {
    const m = new Map();
    const compter = (cle) => m.set(cle, (m.get(cle) || 0) + 1);
    selectedQuestions.forEach((q) => compter(cleItem(q.type_label, q.question_num)));
    generatedItems.forEach((p) => compter(cleItem(p._meta.officielle.typeLabel, p._meta.officielle.questionNum)));
    return m;
  }, [selectedQuestions, generatedItems]);

  const toutRetirer = useCallback(() => {
    setSelectedIds(new Set());
    setSelectedExercices(new Map());
    setShowDrawer(false);
  }, []);

  // Payloads au format unifié pour le LaunchModal/print (jetables, jamais
  // persistés) : les questions en base (le lancement ré-extrait leurs ids via _meta.evalNatId), puis les
  // exercices tirés (enregistrés au lancement seulement).
  const payloadsAdaptes = useMemo(
    () => toPayloadsUnifies(selectedQuestions),
    [selectedQuestions]
  );
  const questionsALancer = useMemo(() => [...payloadsAdaptes, ...generatedItems], [payloadsAdaptes, generatedItems]);
  const sansTexteImprime = useMemo(
    () => selectedQuestions.some((q) => q.type_label?.startsWith('Compréhension'))
      || generatedItems.some((p) => p._meta.officielle.typeLabel?.startsWith('Compréhension')),
    [selectedQuestions, generatedItems]
  );

  // Quitter la composition avec une sélection : la question de QCMDashboard, posée par le bloqueur du routeur
  // (le bouton « Retour à la classe », le retour du navigateur, la barre du haut), comme dans EvalDetail
  const nbChoisisRef = useRef(0);
  nbChoisisRef.current = nbChoisis;
  const questionSortie = useCallback(() => {
    const nb = nbChoisisRef.current;
    return confirm({
      title: 'Quitter la composition ?',
      message: `${nb} exercice${nb > 1 ? 's' : ''} sélectionné${nb > 1 ? 's' : ''} : la sélection sera perdue si vous quittez maintenant.`,
      confirmLabel: 'Quitter sans lancer',
      variant: 'danger',
    });
  }, [confirm]);
  const blocker = useBlockerCompat(nbChoisis > 0);
  useEffect(() => {
    if (!blocker || blocker.state !== 'blocked') return;
    (async () => {
      if (await questionSortie()) blocker.proceed();
      else blocker.reset();
    })();
  }, [blocker, questionSortie]);
  useEffect(() => {
    if (nbChoisis === 0) return undefined;
    const retenir = (e) => { e.preventDefault(); e.returnValue = ''; return e.returnValue; };
    window.addEventListener('beforeunload', retenir);
    return () => window.removeEventListener('beforeunload', retenir);
  }, [nbChoisis]);

  // ─── La fenêtre de QCMDashboard, la même pour créer et pour corriger (11/10/2026) ───
  // Créer : la question est enregistrée tout de suite comme variante de l'item (même Label : comparée avec
  // septembre). Corriger : la même variante, mise à jour sur place (PATCH : même item, mêmes résultats).
  // L'item tient la place de l'attendu, figé (descripteurContext) : la question appartient à CET item. En
  // compréhension de l'écrit, la fenêtre dit avec quel texte la question sera lue.
  const [, doPatch] = usePatchApi();
  const ouvrirCreation = useCallback((officielle) => setFormulaire({ mode: 'creer', officielle }), []);
  const ouvrirCorrection = useCallback((variante) => setFormulaire({ mode: 'corriger', variante }), []);
  const fermerFormulaire = useCallback(() => { setFormulaire(null); setErreurFormulaire(null); }, []);
  const proprietesFormulaire = useMemo(() => {
    if (!formulaire) return null;
    const corriger = formulaire.mode === 'corriger';
    const q = corriger ? formulaire.variante : formulaire.officielle;
    const id = `item-${q.label_id ?? q.question_num}`;
    const item = { attendu: id, attendu_titre: `${q.question_num} · ${q.nom_label || ''}` };
    // Le texte propre d'une variante (un nouveau texte) ; sinon, en lecture, celui de septembre
    const texte = corriger ? supportDeQuestion(q) : null;
    const lecture = String(q.type_label || '').startsWith('Compréhension');
    return {
      question: corriger ? { ...pourLaFenetre(q), ...item } : item,
      descripteurContext: {
        themeName: q.type_label,
        descName: q.question_num,
        attendus: [{ id, description: q.nom_label || '' }],
      },
      texte,
      texteDeSeptembre: lecture && !texte ? nomDuTexte(q.type_label) : null,
    };
  }, [formulaire]);
  const enregistrerFormulaire = useCallback(async (data) => {
    if (!formulaire) return;
    setErreurFormulaire(null);
    if (formulaire.mode === 'corriger') {
      const correction = pourLaCorrection(data, formulaire.variante);
      if (!correction.ok) {
        setErreurFormulaire(`Cette question ne peut pas être enregistrée comme variante de l'item : ${correction.erreur}`);
        return;
      }
      const reponse = await doPatch(formDataCorrection(correction.champs), `${url}questions/${formulaire.variante.id}/`);
      if (!reponse.success) {
        setErreurFormulaire(messageDuServeur(reponse.error, "La variante n'a pas pu être enregistrée."));
        return;
      }
      setNewlyCreatedId(formulaire.variante.id);
    } else {
      let variante;
      try {
        variante = versVarianteEvalNat(data);
      } catch (e) {
        setErreurFormulaire(`Cette question ne peut pas devenir une variante de l'item : ${e.message}`);
        return;
      }
      const reponse = await doPostQuestion(formDataVariante(variante, {
        labelId: formulaire.officielle.label_id,
        etablissementId: etablissementData?.id,
        generatorKey: null,
      }));
      if (!reponse?.success) {
        setErreurFormulaire(messageDuServeur(reponse?.error, "La question n'a pas pu être enregistrée."));
        return;
      }
      setNewlyCreatedId(reponse.data?.id ?? null);
    }
    setFormulaire(null);
    setRefreshQuestions((prev) => prev + 1);
  }, [formulaire, doPatch, doPostQuestion, etablissementData?.id]);

  // Lancer — contrat du LaunchModal unifié (2.4b) :
  // config = { mode_passation, titre, dureeQuiz, datePassation, launchNow,
  //            correctionVisible, questions: payloads ORDONNÉS }.
  // Le mode papier ne passe JAMAIS ici (le modal ouvre l'aperçu PDF lui-même).
  // Les exercices tirés sont enregistrés d'abord (un à un), le QCM ensuite : un échec n'en laisse
  // aucun à moitié, et un nouvel essai reprend les variantes déjà enregistrées (dejaCreesRef).
  const dejaCreesRef = useRef(new Map());
  const handleLaunch = async (config) => {
    setLaunchError(null);
    const titre = (config.titre || '').trim();
    if (listEvalsBilan.some((e) => (e.titre || '').trim() === titre)) {
      setLaunchError(`Un QCM de suivi s'appelle déjà « ${titre} » : changer le titre.`);
      return;
    }
    setLaunchLoading(true);
    // "Maintenant" → datePassation null : le bilan EvalNat exige une date.
    const date = config.datePassation || new Date().toISOString().slice(0, 10);
    const resultat = await enregistrerPuisLancer(config.questions || [], {
      deja: dejaCreesRef.current,
      enregistrerVariante: async (q) => {
        const reponse = await doPostQuestion(formDataVariante(versVarianteEvalNat(q), {
          labelId: q._meta.officielle.labelId,
          etablissementId: etablissementData?.id,
          generatorKey: q._meta.generatorKey,
        }));
        if (!reponse?.success) {
          const message = reponse?.error?.response?.data?.error;
          throw new Error(typeof message === 'string' ? message : "la variante n'a pas pu être enregistrée.");
        }
        return reponse.data?.id;
      },
      creerQcm: async (questionIds) => {
        const reponse = await doPost({
          titre: config.titre,
          date,
          ...(groupNom ? { groupe: idclasse } : { classe: idclasse }),
          questions: questionIds,
          duree: config.dureeQuiz,
          type: 'class',
          etablissement: etablissementData?.id,
          correction_visible: config.correctionVisible,
        });
        return reponse?.success
          ? { ok: true, data: reponse.data }
          : { ok: false, erreur: reponse?.error?.message || reponse?.error || 'Erreur lors de la création' };
      },
    });
    setLaunchLoading(false);
    if (resultat.ok) {
      setShowLaunchModal(false);
      setSelectedIds(new Set());
      setSelectedExercices(new Map());
      setShowDrawer(false);
      dejaCreesRef.current = new Map();
      setSuccessInfo({ id: resultat.resultat.data?.id, titre: config.titre });
      setRefreshQuestions((prev) => prev + 1);
      handleReloadEvalNat();
    } else {
      setLaunchError(resultat.erreur);
    }
  };

  // ─── Le bandeau collant de QCMDashboard : retour à la classe, classe · matière, item ouvert ───
  const titreMatiere = matiere ? ` · ${matiere.charAt(0).toUpperCase()}${matiere.slice(1)}` : '';
  const libelleCourant = (itemCourant?.officielle || itemCourant?.variantes[0])?.nom_label || '';
  const bandeau = (avecItems) => (
    <div className="sticky top-[57px] z-40 -mx-4 -mt-5 px-6 lg:px-10 py-2.5 bg-[#F1F5FA] border-b-2 border-[#D7E8F8]">
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate(`/home/creer/${idclasse}`)}
          className="shrink-0 mt-0.5 flex items-center gap-1 px-3 py-1.5 rounded-lg border border-orange-300 bg-white text-orange-600 text-xs font-semibold hover:bg-orange-50 transition"
        >
          <ChevronLeft size={15} />
          Retour à la classe
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-extrabold text-[#0f2a4a] tracking-tight leading-tight truncate">
            {nomRef || 'QCM de suivi'}{titreMatiere}
          </h1>
          {avecItems ? (
            <div className="mt-0.5 flex items-end gap-3">
              <div className="flex-1 min-w-0">
                {itemCourant ? (
                  <>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold truncate">
                      QCM de suivi · {itemCourant.typeLabel}
                    </p>
                    <p className="text-[15px] font-bold text-[#0f2a4a] truncate">
                      <span className="font-mono text-[11px] font-bold px-1.5 py-[1px] mr-1.5 rounded-md bg-blue-50 text-blue-600 border border-blue-100 align-middle">
                        {itemCourant.questionNum}
                      </span>
                      {libelleCourant}
                    </p>
                  </>
                ) : surForme ? (
                  <>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold truncate">
                      QCM de suivi · Compréhension de l&apos;écrit
                    </p>
                    <p className="text-[15px] font-bold text-[#0f2a4a] truncate">
                      Forme parallèle : {forme.textes.map((t) => `« ${t.SUPPORT.titre} »`).join(' et ')}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">Cherchez un item de l'évaluation nationale, ou parcourez les tests.</p>
                )}
              </div>
              {/* La recherche des items ouvre le menu, filtré ; le menu reste à un clic (icône du champ) */}
              <div className="relative shrink-0 w-[230px]">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-blue-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition">
                  <Search size={14} className="shrink-0 text-blue-500" />
                  <input
                    type="search"
                    value={recherche}
                    onChange={(e) => { setRecherche(e.target.value); setMenuOuvert(true); }}
                    onKeyDown={(e) => { if (e.key === 'Escape') setRecherche(''); }}
                    aria-label="Rechercher un item"
                    placeholder="Rechercher un item…"
                    className="w-full min-w-0 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setMenuOuvert(true)}
                    title="Parcourir les items"
                    aria-label="Parcourir les items"
                    className="shrink-0 -mr-1 p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition"
                  >
                    <ListTree size={14} />
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {evalsEnCours.length > 0 && (
          <div className="shrink-0 flex flex-col items-end gap-1">
            <button
              onClick={() => navigate(`/home/evaluations/${idclasse}`)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition"
            >
              <Play size={11} className="animate-pulse" />
              {evalsEnCours.length} évaluation{evalsEnCours.length > 1 ? 's' : ''} en cours · Reprendre →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Détail d'un bilan (vue existante) ───
  if (selectedEvalId) {
    return (
      <DetailEvalBilan
        evalBilan={evalBilan}
        setEvalBilan={setEvalBilan}
        selectedEvalId={selectedEvalId}
        setSelectedEvalId={(val) => {
          setSelectedEvalId(val);
          // Fermer le détail ramène à la liste unifiée (l'historique local
          // n'existe plus), d'où qu'on soit venu (?bilan= ou succès création).
          if (!val) navigate(`/home/evaluations/${idclasse}`);
        }}
        EvalNat={EvalNatRef}
        onReload={handleReloadEvalNat}
      />
    );
  }

  // ─── EvalNat manquante ───
  if (!EvalNatRef) {
    return (
      <div className="px-4 mt-5 max-w-full">
        {ConfirmDialog}
        {bandeau(false)}
        <div className="flex flex-col mt-10 gap-8">
          <div className="w-full max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-amber-100 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Évaluation nationale requise</h2>
                    <p className="text-white/80 text-sm">Étape préalable obligatoire</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-3 mb-5">
                  <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                    <FileSpreadsheet className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-gray-800 font-medium mb-1">Données manquantes</p>
                    <p className="text-gray-600 text-sm">
                      Aucune évaluation nationale n'a été détectée pour cette classe.
                      Les QCM de suivi nécessitent les données de l'évaluation nationale comme base de référence.
                    </p>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ClipboardList className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-blue-800 text-sm">Comment procéder ?</span>
                  </div>
                  <ol className="space-y-2 text-sm text-blue-700">
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      <span>Accédez au menu <strong>Évaluation Nationale</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      <span>Importez les résultats de l'évaluation nationale</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      <span>Revenez ici pour créer vos évaluations bilans</span>
                    </li>
                  </ol>
                </div>
                <button
                  onClick={() => navigate(`/home/eval-nat/${idclasse}`)}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 transform hover:scale-[1.02] transition-all duration-200"
                >
                  Accéder à l'évaluation nationale
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── La composition ───
  const nbItems = groupes.reduce((n, g) => n + g.items.length, 0);
  return (
    <div className="px-4 mt-5 max-w-full">
      {ConfirmDialog}
      <div className="">
        {bandeau(true)}

        {/* Contenu principal */}
        <div className="mt-5 space-y-4">

          {/* Pastille succès création */}
          {successInfo && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
              <CheckCircle size={16} className="shrink-0" />
              <span className="font-medium">QCM de suivi « {successInfo.titre} » créé : retrouvez-le dans Évaluations</span>
              <button
                onClick={() => {
                  const found = listEvalsBilan.find(e => e.id === successInfo.id);
                  if (found) {
                    setSelectedEvalId(found.id);
                    setEvalBilan(found);
                  } else {
                    navigate(`/home/evaluations/${idclasse}`);
                  }
                  setSuccessInfo(null);
                }}
                className="ml-auto text-xs text-emerald-500 hover:text-emerald-700 font-medium transition"
              >
                Voir →
              </button>
              <button
                onClick={() => setSuccessInfo(null)}
                className="text-emerald-300 hover:text-emerald-500 transition"
              >
                ×
              </button>
            </div>
          )}

          {/* L'item ouvert, ou l'état vide de QCMDashboard */}
          {loadingQuestions && groupes.length === 0 ? (
            <p className="text-sm text-slate-500">Chargement des questions…</p>
          ) : groupes.length === 0 && !formeDisponible ? (
            <p className="text-sm text-slate-500">Aucune question de l'évaluation nationale pour ce niveau.</p>
          ) : surForme ? (
            <FormeParalleleEvalNat
              forme={forme}
              questionsParTexte={questionsParTexte}
              selectedIds={selectedIds}
              onBasculer={basculerQuestions}
              onPreview={handlePreview}
            />
          ) : itemCourant ? (
            <ItemEvalNat
              key={cleItem(itemCourant.typeLabel, itemCourant.questionNum)}
              item={itemCourant}
              niveau={niveau}
              niveauForm={niveauForm}
              matiere={matiere}
              selectedExercices={selectedExercices}
              onUpdateExercice={handleUpdateExercice}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onPreview={handlePreview}
              onApercu={handleApercu}
              onEditVariante={ouvrirCorrection}
              onDeleteVariante={handleDeleteVariante}
              onCreateQuestion={ouvrirCreation}
              nouveauId={newlyCreatedId}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <BookOpen size={48} className="text-blue-500 mb-4" />
              <h3 className="text-xl font-extrabold text-slate-800 mb-2">Composez votre QCM de suivi</h3>
              <p className="text-[15px] font-semibold text-slate-600 mb-6 max-w-md leading-relaxed">
                Choisissez un item de l'évaluation nationale dans le panneau de gauche. Sa question officielle et ses variantes s'afficheront ici, prêtes à être ajoutées.
              </p>
              {!menuOuvert && (
                <button
                  onClick={() => setMenuOuvert(true)}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <Search size={16} /> Ouvrir le panneau des items
                </button>
              )}
            </div>
          )}

          {/* Le menu des tests et des items */}
          <MenuItemsEvalNat
            open={menuOuvert}
            onClose={() => setMenuOuvert(false)}
            groupes={groupesFiltres}
            recherche={recherche}
            onRecherche={setRecherche}
            itemActif={itemCourant ? cleItem(itemCourant.typeLabel, itemCourant.questionNum) : surForme ? CLE_FORME : null}
            onChoisir={choisirItem}
            comptes={comptes}
            sousTitre={`${niveau}e · ${LIBELLE_MATIERE[normalizeMatiere(matiere)] || 'Évaluation'} · ${nbItems} items`}
            totalChoisies={nbChoisis}
            formeParallele={formeDisponible ? {
              cle: CLE_FORME,
              titre: "Compréhension de l'écrit",
              sousTitre: `D'autres textes que septembre : ${forme.textes.map((t) => `« ${t.SUPPORT.titre} »`).join(' et ')}`,
              compte: nbForme,
            } : null}
            onChoisirForme={choisirForme}
          />
        </div>
      </div>

      {/* Un exercice modifié refusé à l'ajout : le message reste au-dessus de la barre du bas */}
      {erreurAjout ? (
        <div className="fixed bottom-24 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
          <div role="alert" className="pointer-events-auto max-w-xl flex items-start gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm shadow-lg">
            <span className="flex-1">Cet exercice ne peut pas devenir une variante de l'item. {erreurAjout}</span>
            <button onClick={() => setErreurAjout(null)} aria-label="Fermer" className="text-red-300 hover:text-red-500 transition">
              <X size={14} />
            </button>
          </div>
        </div>
      ) : null}

      {/* ═══ STICKY SELECTION BAR + DRAWER PANIER (ceux de QCMDashboard) ═══ */}
      {nbChoisis > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col items-center pointer-events-none">

          {/* Drawer panier — slide-up quand ouvert */}
          {showDrawer && (
            <div className="pointer-events-auto w-full max-w-2xl mx-4 mb-2 bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden" style={{ animation: 'slideUp 0.2s ease-out' }}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">Votre QCM de suivi</span>
                <button onClick={() => setShowDrawer(false)} className="text-xs text-gray-400 hover:text-gray-600 transition">Fermer</button>
              </div>
              <div className="max-h-[40vh] overflow-auto divide-y divide-gray-50">
                {/* Exercices TanoEd */}
                {[...selectedExercices.entries()].map(([key, { payloads }]) => {
                  const count = payloads.length;
                  const payload = payloads[0];
                  if (!payload) return null;
                  const label = getQuestionTypeLabel(payload);
                  const desc = sansBalises(payload.texte).slice(0, 60) || 'Exercice';
                  return (
                    <div key={key} className="flex items-center gap-3 px-4 py-2 text-sm">
                      <span className="px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 text-[10px] font-semibold flex-shrink-0">{label}</span>
                      <span className="flex-1 text-gray-600 truncate">{payload._meta.officielle.questionNum} · {desc}</span>
                      {count > 1 && <span className="text-xs font-semibold text-gray-500">×{count}</span>}
                      <button
                        onClick={() => handleUpdateExercice(key, 0, null)}
                        className="text-red-300 hover:text-red-500 transition flex-shrink-0"
                        title="Retirer"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
                {/* Questions en base : l'officielle, une question de la forme parallèle, ou une variante du
                    collège (« Exerciseur » si tirée) */}
                {selectedQuestions.map((q) => {
                  const desc = sansBalises(q.texte).slice(0, 60) || q.nom_label || 'Question';
                  const deLaForme = estDeLaForme(q);
                  const genre = q.est_nationale ? 'Officielle' : deLaForme ? 'Forme parallèle' : q.generator_key ? 'Exerciseur' : 'Prof';
                  const couleur = q.est_nationale
                    ? 'bg-emerald-100 text-emerald-700'
                    : deLaForme ? 'bg-sky-100 text-sky-700' : q.generator_key ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700';
                  return (
                    <div key={q.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold flex-shrink-0 ${couleur}`}>{genre}</span>
                      <span className="flex-1 text-gray-600 truncate">{q.question_num} · {desc}</span>
                      <button
                        onClick={() => handleToggleSelect(q.id)}
                        className="text-red-300 hover:text-red-500 transition flex-shrink-0"
                        title="Retirer"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
              {sansTexteImprime ? (
                <p className="px-4 py-2 border-t border-gray-100 text-[11px] text-amber-700">L'éval papier n'imprime pas le texte à lire.</p>
              ) : null}
            </div>
          )}

          {/* Sticky bar */}
          <div className="p-4 w-full flex justify-center">
            <div
              key={nbChoisis}
              className="pointer-events-auto flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 rounded-2xl bg-white border border-gray-200 shadow-2xl"
              style={{ animation: 'badgePulse 0.25s ease-out' }}
            >
              {/* Détail cliquable — ouvre le drawer */}
              <button
                onClick={() => setShowDrawer(prev => !prev)}
                className="text-xs sm:text-sm text-gray-700 flex items-center gap-1.5 flex-wrap hover:text-blue-600 transition"
              >
                {formatDetail.total > 0 && (
                  <strong>{formatDetail.total} exercice{formatDetail.total > 1 ? 's' : ''}</strong>
                )}
                {formatDetail.total > 0 && formatDetail.detail && (
                  <span className="text-gray-400 hidden sm:inline">({formatDetail.detail})</span>
                )}
                {partiesBanque.map((partie, i) => (
                  <Fragment key={partie}>
                    {formatDetail.total > 0 || i > 0 ? <span className="text-gray-300">·</span> : null}
                    <strong className="text-[#0f2a4a]">{partie}</strong>
                  </Fragment>
                ))}
              </button>
              <button
                onClick={toutRetirer}
                className="text-[10px] sm:text-xs text-gray-400 hover:text-gray-600 transition"
              >
                Tout retirer
              </button>
              {/* Pas de « Lancer avec les cartes » : le QCM de suivi se passe en ligne ou sur papier */}
              <button
                onClick={() => { setLaunchFormat('en_ligne'); setShowLaunchModal(true); }}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-white font-semibold text-xs sm:text-sm shadow-md transition bg-teal-600 hover:bg-teal-700 shadow-teal-600/20"
              >
                <Monitor size={15} />
                <span className="hidden sm:inline">Faire en ligne</span>
                <span className="sm:hidden">En ligne</span>
              </button>
              {/* Éval papier */}
              <button
                onClick={() => { setLaunchFormat('papier'); setShowLaunchModal(true); }}
                title={sansTexteImprime ? "L'éval papier n'imprime pas le texte à lire" : undefined}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-xs sm:text-sm shadow-md shadow-amber-500/20 transition"
              >
                <Printer size={15} />
                <span className="hidden sm:inline">Générer l'éval papier</span>
                <span className="sm:hidden">Papier</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de lancement — LaunchModal UNIFIÉ (2.4b), CRÉATION uniquement
          (l'édition d'un bilan existant vit dans la liste Évaluations) :
          - questions = payloads adaptés (format unifié, jetables), puis les exercices tirés ;
          - withTitre : les bilans EvalNat ont un titre (requis serializer) ;
          - initialConfig : titre horodaté, lu UNE fois au montage ;
          - printContext réduit à nomClasse : remplit le champ Classe de la
            feuille SANS activer le panneau de suivi (garde sur idclasse). */}
      {showLaunchModal && (
        <LaunchModal
          launchMode={launchFormat}
          availableFormats={FORMATS_EVALNAT}
          selectedQuestions={questionsALancer}
          withTitre
          initialConfig={{ titre: `Bilan du ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` }}
          printContext={{ nomClasse: nomRef }}
          cartes={LISTE_VIDE}
          eleves={LISTE_VIDE}
          isLoading={launchLoading}
          error={launchError}
          onLaunch={handleLaunch}
          onClose={() => { setShowLaunchModal(false); setLaunchError(null); }}
        />
      )}

      {/* Aperçu d'une question en base */}
      {previewQuestion && (
        <QuestionPreviewModal
          question={previewQuestion}
          onClose={() => setPreviewQuestion(null)}
        />
      )}

      {/* Aperçu élève d'un exercice tiré (« Autre variante » dans la fenêtre) */}
      {apercu && (
        <ApercuEleveModal
          question={apercu.question}
          regenerate={apercu.regenerate}
          onClose={() => setApercu(null)}
        />
      )}

      {/* ═══ LA FENÊTRE DE QCMDASHBOARD : créer une question, corriger une variante ═══ */}
      {proprietesFormulaire && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={fermerFormulaire}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto p-5" onClick={e => e.stopPropagation()}>
            {erreurFormulaire ? (
              <p role="alert" className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{erreurFormulaire}</p>
            ) : null}
            {proprietesFormulaire.texte ? <LigneTexteLecture support={proprietesFormulaire.texte} /> : null}
            {proprietesFormulaire.texteDeSeptembre ? (
              <p className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-800">
                <BookOpen size={15} className="shrink-0" />
                <span>Votre question sera lue avec le texte de septembre : <b>{proprietesFormulaire.texteDeSeptembre}</b>.</span>
              </p>
            ) : null}
            <QuestionForm
              question={proprietesFormulaire.question}
              niveau={niveauForm}
              matiere={matiere}
              niveauChar={niveau}
              descripteurContext={proprietesFormulaire.descripteurContext}
              onSubmit={enregistrerFormulaire}
              onCancel={fermerFormulaire}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default EvalBilan;
